/** A little-endian cursor over one section of an ELF file. */
export class Reader {
    readonly bytes: Uint8Array;
    readonly #view: DataView;
    offset: number;

    constructor(bytes: Uint8Array, offset = 0) {
        this.bytes = bytes;
        this.#view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        this.offset = offset;
    }

    get done(): boolean {
        return this.offset >= this.bytes.length;
    }

    u8(): number {
        return this.#view.getUint8(this.offset++);
    }

    i8(): number {
        return this.#view.getInt8(this.offset++);
    }

    u16(): number {
        const value = this.#view.getUint16(this.offset, true);
        this.offset += 2;
        return value;
    }

    u24(): number {
        return this.u16() | (this.u8() << 16);
    }

    u32(): number {
        const value = this.#view.getUint32(this.offset, true);
        this.offset += 4;
        return value;
    }

    /** The low 32 bits; nothing here addresses more than a 32-bit chip does. */
    u64(): number {
        const low = this.u32();
        this.offset += 4;
        return low;
    }

    /** An unsigned integer of `size` bytes: an address or a section offset. */
    sized(size: number): number {
        switch (size) {
            case 1:
                return this.u8();
            case 2:
                return this.u16();
            case 4:
                return this.u32();
            case 8:
                return this.u64();
        }
        throw new Error(`unsupported field size ${size}`);
    }

    uleb(): number {
        let result = 0;
        let scale = 1;
        for (;;) {
            const byte = this.u8();
            result += (byte & 0x7f) * scale;
            scale *= 128;
            if ((byte & 0x80) === 0) return result;
        }
    }

    sleb(): number {
        let result = 0;
        let scale = 1;
        for (;;) {
            const byte = this.u8();
            result += (byte & 0x7f) * scale;
            scale *= 128;
            if ((byte & 0x80) === 0) return byte & 0x40 ? result - scale : result;
        }
    }

    /** A NUL-terminated string. */
    cstr(): string {
        const end = this.bytes.indexOf(0, this.offset);
        const stop = end < 0 ? this.bytes.length : end;
        const text = decodeText(this.bytes.subarray(this.offset, stop));
        this.offset = stop + 1;
        return text;
    }

    skip(count: number): void {
        this.offset += count;
    }
}

const TEXT = new TextDecoder();

export function decodeText(bytes: Uint8Array): string {
    return TEXT.decode(bytes);
}

/** The NUL-terminated string at `offset` of a string section. */
export function stringAt(bytes: Uint8Array, offset: number): string {
    return new Reader(bytes, offset).cstr();
}
